import posthog from 'posthog-js';
import { captureAttribution, readAttribution } from '@/lib/attribution';

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST = process.env.NEXT_PUBLIC_POSTHOG_HOST || 'https://us.i.posthog.com';
const IS_PRODUCTION = process.env.NODE_ENV === 'production';

let _initialized = false;

function isLikelyBot(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  return /bot|crawler|spider|crawling|headless|phantom|selenium/i.test(ua)
    || !ua
    || ua === 'unknown';
}

export function initPostHog(): void {
  if (typeof window === 'undefined' || _initialized || !POSTHOG_KEY) return;
  if (isLikelyBot()) return;

  posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    autocapture: IS_PRODUCTION,
    capture_pageview: false,
    capture_pageleave: true,
    capture_exceptions: true,
    person_profiles: 'identified_only',
    session_recording: IS_PRODUCTION ? {
      maskAllInputs: true,
      maskTextSelector: '[data-ph-mask]',
    } : undefined,
  });

  _initialized = true;
}

export function getPostHog() {
  if (!_initialized || typeof window === 'undefined') return null;
  return posthog;
}

export function trackPageView(path: string): void {
  const fullUrl = typeof window !== 'undefined'
    ? `${window.location.origin}${path}`
    : path;

  getPostHog()?.capture('$pageview', {
    $current_url: fullUrl,
    hostname: typeof window !== 'undefined'
      ? window.location.hostname
      : 'clabs.ctribefestival.com',
  });
}

export function identifyUser(uid: string, properties?: Record<string, string>): void {
  getPostHog()?.identify(uid, properties);
}

export function resetUser(): void {
  getPostHog()?.reset();
}

export function trackFormEvent(
  action: 'start' | 'progress' | 'submit' | 'success' | 'error',
  formSlug: string,
  metadata?: Record<string, string | number | boolean>,
): void {
  // Campaign tags from the landing visit, so form funnels split by ad.
  // The form page can remount while sign-in settles; count one start per
  // form per browser session so funnel conversion rates stay honest.
  if (action === 'start' && typeof window !== 'undefined') {
    try {
      const key = `ph_form_start_${formSlug}`;
      if (window.sessionStorage.getItem(key)) return;
      window.sessionStorage.setItem(key, '1');
    } catch {
      // Storage blocked: fall through and record the start.
    }
  }
  // Capture first: the form page's effects can run before AttributionCapture's.
  captureAttribution();
  const attribution = readAttribution();
  getPostHog()?.capture(`form_${action}`, {
    form_slug: formSlug,
    ...(attribution
      ? {
          utm_source: attribution.utm_source,
          utm_medium: attribution.utm_medium,
          utm_campaign: attribution.utm_campaign,
          utm_content: attribution.utm_content,
          utm_term: attribution.utm_term,
          landing_path: attribution.landingPath,
        }
      : {}),
    hostname: typeof window !== 'undefined'
      ? window.location.hostname
      : 'clabs.ctribefestival.com',
    ...metadata,
  });
}

/**
 * Pass checkout funnel event with the visit's campaign tags (utm_*). Use
 * beforeNavigation when the next line leaves the page (e.g. Stripe redirect).
 */
export function trackCheckoutEvent(
  event: string,
  metadata: Record<string, string | number | boolean | null | undefined> = {},
  beforeNavigation = true,
): void {
  captureAttribution();
  const attribution = readAttribution();
  getPostHog()?.capture(event, {
    ...(attribution
      ? {
          utm_source: attribution.utm_source,
          utm_medium: attribution.utm_medium,
          utm_campaign: attribution.utm_campaign,
          utm_content: attribution.utm_content,
          utm_term: attribution.utm_term,
          landing_path: attribution.landingPath,
        }
      : {}),
    hostname: typeof window !== 'undefined' ? window.location.hostname : 'clabs.ctribefestival.com',
    ...metadata,
  }, beforeNavigation ? { transport: 'sendBeacon' } : undefined);
}
